import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader2, Users, Bot, Trophy, Zap, Swords } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

// UX polish: hold "Finding opponent…" for at least this long so the
// matchmaking spinner doesn't flash before instantly disappearing.
const SEARCH_MIN_MS = 1200;

/**
 * Matchmaking overlay for Ranked / Casual PvP (single-card tap combat).
 * - Multi-card players pick their fighter first.
 * - Backend picks a real human via the matchmaking priority ladder, or
 *   falls back to an AI Explorer if no human is available.
 * - "Play vs AI Explorer" button bypasses the human tiers explicitly.
 * On success, the caller's `onMatchFound(battleId)` handler redirects
 * to /battle/live/{battleId}.
 */
export default function MatchmakingModal({ open, mode, cards, onClose, onMatchFound }) {
  const [status, setStatus] = useState('idle'); // idle | picking | searching | matched | error
  const [error, setError] = useState(null);
  const [pickedCardId, setPickedCardId] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const closedRef = useRef(false);
  const cardList = useMemo(() => (Array.isArray(cards) ? cards : []), [cards]);

  const finalize = useCallback((battleId) => {
    closedRef.current = true;
    onMatchFound?.(battleId);
  }, [onMatchFound]);

  // Core battle-start call. `forceAI` skips human tiers on the backend.
  const startBattle = useCallback(async (cardId, { forceAI = false } = {}) => {
    if (!cardId) return;
    setStatus('searching'); setError(null);
    if (forceAI) setAiBusy(true);
    const searchStartedAt = Date.now();
    try {
      const body = {
        challenger_card_id: cardId,
        best_of: 5,
        mode,
        arena: 'forest',
        weather: 'clear',
      };
      if (forceAI) body.force_ai_opponent = true;

      const { data } = await api.post('/battles', body);
      const battleId = data?.battle_id;
      if (!battleId) {
        setError('Server returned no battle id.'); setStatus('error');
        return;
      }
      // Enforce the minimum spinner duration so the UI doesn't flash
      const elapsed = Date.now() - searchStartedAt;
      const remaining = Math.max(0, SEARCH_MIN_MS - elapsed);
      setTimeout(() => {
        setStatus('matched');
        finalize(battleId);
      }, remaining);
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = typeof d === 'string' ? d : (d?.message || 'Could not start battle');
      setError(msg); setStatus('error');
    } finally {
      if (forceAI) setAiBusy(false);
    }
  }, [mode, finalize]);

  useEffect(() => {
    if (!open) {
      closedRef.current = false;
      setPickedCardId(null);
      setStatus('idle');
      setError(null);
      return;
    }
    if (cardList.length === 1) {
      setPickedCardId(cardList[0].card_id);
      startBattle(cardList[0].card_id);
    } else if (cardList.length > 1) {
      setStatus('picking');
    } else {
      setStatus('error');
      setError('You need at least one Battle Card to queue for PvP.');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleCardPick = (cardId) => {
    setPickedCardId(cardId);
    startBattle(cardId);
  };

  const takeAiFallback = () => {
    const cardId = pickedCardId || cardList[0]?.card_id;
    if (!cardId) {
      toast.error('Pick a card first.');
      return;
    }
    startBattle(cardId, { forceAI: true });
  };

  if (!open) return null;

  const handleCancel = () => onClose?.();

  const isRanked = mode === 'ranked';
  const Icon = isRanked ? Trophy : Users;
  const modeLabel = isRanked ? 'Ranked PvP' : 'Casual PvP';
  const accentColor = isRanked ? 'text-amber-300' : 'text-teal-300';
  const accentBg = isRanked ? 'from-amber-500/20' : 'from-teal-500/20';

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
        data-testid="matchmaking-modal"
      >
        <motion.div
          initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95 }}
          className={`relative w-full max-w-md rounded-3xl border border-white/10 bg-gradient-to-b ${accentBg} to-[#04120c] p-6 space-y-4`}
        >
          <button
            type="button" onClick={handleCancel}
            data-testid="matchmaking-close-btn"
            aria-label="Cancel matchmaking"
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/40 hover:bg-black/60 flex items-center justify-center text-white/70"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="flex items-center gap-3">
            <div className={`w-11 h-11 rounded-2xl bg-black/40 flex items-center justify-center ${accentColor}`}>
              <Icon className="w-6 h-6" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-white/60">Finding an Explorer</p>
              <h2 className="font-serif text-2xl leading-tight" data-testid="matchmaking-mode-label">{modeLabel}</h2>
            </div>
          </div>

          {status === 'error' && (
            <div className="rounded-xl bg-rose-500/10 border border-rose-400/30 p-3 text-xs text-rose-200" data-testid="matchmaking-error">
              {error || 'Something went wrong.'}
            </div>
          )}

          {status === 'picking' && (
            <div className="space-y-2" data-testid="matchmaking-card-picker">
              <p className="text-[10px] uppercase tracking-widest text-white/60 flex items-center gap-1.5">
                <Swords className="w-3 h-3" /> Choose your fighter
              </p>
              <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
                {cardList.map(c => (
                  <button
                    key={c.card_id}
                    type="button"
                    onClick={() => handleCardPick(c.card_id)}
                    data-testid={`matchmaking-card-option-${c.card_id}`}
                    className="w-full text-left rounded-xl p-3 border border-white/10 bg-white/5 hover:border-accent/40 hover:bg-white/10 transition-colors flex items-center gap-2.5"
                  >
                    <div className="w-9 h-9 rounded-lg bg-black/40 overflow-hidden flex items-center justify-center shrink-0">
                      <Swords className="w-4 h-4 text-white/60" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">{c.species_common_name}</p>
                      <p className="text-[10px] text-white/50 mt-0.5 uppercase tracking-wider">
                        {c.rarity} · rating {c.battle_rating || '—'}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {status === 'searching' && (
            <div className="rounded-2xl bg-black/40 border border-white/5 p-4 flex items-center gap-3" data-testid="matchmaking-searching">
              <Loader2 className="w-5 h-5 animate-spin text-white/70 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white/90">
                  {aiBusy ? 'Summoning an AI Explorer…' : 'Searching for an Explorer…'}
                </p>
                <p className="text-[11px] text-white/50 mt-0.5">
                  We match with a live player when one is nearby, otherwise an AI stand-in steps up.
                </p>
              </div>
            </div>
          )}

          {status === 'matched' && (
            <div className="rounded-2xl bg-emerald-500/10 border border-emerald-400/30 p-4 flex items-center gap-3" data-testid="matchmaking-matched">
              <Zap className="w-5 h-5 text-emerald-300" />
              <p className="text-sm text-emerald-200">Match found! Loading Arena…</p>
            </div>
          )}

          <div className="flex flex-col gap-2 pt-1">
            {status !== 'picking' && status !== 'matched' && (
              <button
                type="button"
                onClick={takeAiFallback}
                disabled={aiBusy || status === 'searching' || status === 'error'}
                data-testid="matchmaking-play-ai-btn"
                className="w-full px-4 py-3 rounded-full bg-primary/90 hover:bg-primary text-white text-xs font-semibold uppercase tracking-widest flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {aiBusy
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Preparing AI…</>
                  : <><Bot className="w-4 h-4" /> Play vs AI Explorer instead</>}
              </button>
            )}
            <button
              type="button"
              onClick={handleCancel}
              disabled={status === 'matched'}
              data-testid="matchmaking-cancel-btn"
              className="w-full px-4 py-2 rounded-full border border-white/15 hover:bg-white/5 text-white/70 text-xs uppercase tracking-widest disabled:opacity-40"
            >
              Cancel
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
